import { useQuery } from '@apollo/client';
import { Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ManageDataGrid } from '~/components/data-grid';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { i18n } from '~/lib/i18n';
import { PlaylistDynamicFiltersDocument } from '../../queries/queries.generated';
import { useTagUtils } from '../lib';

export interface TagsDialogProps {
  onClose: () => void;
  open: boolean;
}

export const TagsDialog = ({ onClose, open }: TagsDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(PlaylistDynamicFiltersDocument, {
    variables: {
      currentNetworkId: currentNetwork.id,
    },
    fetchPolicy: 'cache-first',
  });

  const {
    cellModesModel,
    columns,
    confirmDeleteProps,
    handleCellEditStart,
    handleCellEditStop,
    handleCellFocus,
    handleCellKeyDown,
    processRowUpdate,
    rows,
    selectedCellParams,
    setCellModesModel,
  } = useTagUtils(data?.network?.playlistTags || []);

  const selectedRow = useMemo(
    () => rows.find((x) => x.id === selectedCellParams?.id),
    [rows, selectedCellParams],
  );

  const deletePromptHtml = useMemo(() => {
    if (!selectedRow) return '';

    const playlistText = t('pages.playlists.thing_plural', { count: selectedRow.uses });

    return i18n.t('pages.playlists.list.tags_dialog.delete_prompt', {
      name: selectedRow.name,
      uses: selectedRow.uses,
      playlistText,
    });
  }, [selectedRow, t]);

  return (
    <Dialog onClose={onClose} fullWidth maxWidth="sm" open={open}>
      <DialogTitle onClose={onClose}>{t('common.tags.manage_tags')}</DialogTitle>
      <DialogContent>
        {rows.length > 0 ? (
          <ManageDataGrid
            cellModesModel={cellModesModel}
            columns={columns}
            disableColumnMenu
            disableRowSelectionOnClick
            onCellEditStart={handleCellEditStart}
            onCellEditStop={handleCellEditStop}
            onCellKeyDown={handleCellKeyDown}
            onCellModesModelChange={setCellModesModel}
            processRowUpdate={processRowUpdate}
            rows={rows}
            slots={{
              columnResizeIcon: () => null,
            }}
            slotProps={{
              cell: {
                onFocus: handleCellFocus,
              },
            }}
            localeText={{
              footerTotalRows: t('common.pagination.total_rows'),
            }}
          />
        ) : (
          <Typography variant="subtitle2" sx={{ paddingY: 2 }}>
            {t('common.tags.no_tags')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.dialogs.close')}
        </Button>
      </DialogActions>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.playlists.list.tags_dialog.delete_confirm')}
        cancel={t('common.dialogs.cancel')}
        deleteConfirm
        prompt={<span dangerouslySetInnerHTML={{ __html: deletePromptHtml }} />}
        title={t('common.tags.delete_tag')}
      />
    </Dialog>
  );
};
